"use client"

import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../redux/main/authSlice";
import { selectAccountData } from "../../redux/main/accountSlice";
import PageHeader from "../../components/PageHeader";
import Spinner from "../../components/Spinner";
import Alert from "../../components/Alert";
import { apiService } from "../../utils/apiService";
import { formatCurrency, formatDate } from "../../utils/formatters";

export default function CustomerPayments() {
  const authenticatedUser = useSelector(selectUser);
  const accountData = useSelector(selectAccountData);
  const accountId = accountData?._id;
  
  // Recuperiamo l'ID cliente da localStorage per assicurarci di utilizzare lo stesso ID usato in altre parti dell'app
  const userData = JSON.parse(localStorage.getItem('installamelo-auth-user') || '{}');
  const customerId = userData?.customerId || authenticatedUser?.customerId;
  
  // Log per debug
  console.log('Dati utente per pagamenti:', { 
    customerId, 
    accountId,
    userData
  });

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Verifica se viene da un pagamento completato con Stripe
  useEffect(() => {
    const checkPaymentStatus = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const success = urlParams.get('success');
      const paymentId = urlParams.get('payment_id');
      
      // Se è stato reindirizzato da Stripe con successo e c'è un ID pagamento
      if (success === 'true' && paymentId) {
        console.log('Rilevato redirect da Stripe con pagamento completato:', paymentId);
        try {
          // Aggiorniamo lo stato del pagamento
          const updateResponse = await fetch(
            `${import.meta.env.VITE_API_BASE_URL}/customerPayments/update-payment-status`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'auth-token': localStorage.getItem('installamelo-auth-token')
              },
              body: JSON.stringify({
                paymentId,
                status: 'completed',
                paid: true,
                stripeStatus: 'succeeded',
                transactionDetails: {
                  completedAt: new Date().toISOString(),
                  completedBy: authenticatedUser?.email || 'cliente',
                  completionMethod: 'stripe_redirect',
                  urlParams: Object.fromEntries(urlParams.entries()),
                  browserInfo: {
                    userAgent: navigator.userAgent,
                    language: navigator.language,
                    platform: navigator.platform
                  }
                }
              })
            }
          );
          
          if (updateResponse.ok) {
            console.log('Pagamento aggiornato con successo');
            
            // Mostriamo un banner di successo
            const successDiv = document.createElement('div');
            successDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg z-50';
            successDiv.innerHTML = `
              <div class="flex items-center">
                <svg class="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span>Pagamento completato con successo!</span>
              </div>
            `;
            document.body.appendChild(successDiv);
            
            // Rimuoviamo il banner dopo 5 secondi
            setTimeout(() => {
              if (document.body.contains(successDiv)) {
                document.body.removeChild(successDiv);
              }
            }, 5000);
            
            // Rimuoviamo i parametri dalla URL per evitare doppi aggiornamenti in caso di refresh
            window.history.replaceState({}, document.title, window.location.pathname);
          } else {
            console.error('Errore nell\'aggiornamento del pagamento:', await updateResponse.text());
          }
        } catch (error) {
          console.error('Errore durante l\'aggiornamento dello stato del pagamento:', error);
        }
      }
    };
    
    checkPaymentStatus();
  }, [authenticatedUser]);
  
  // Carica i pagamenti del cliente
  const loadPayments = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!customerId) {
        throw new Error("ID cliente non disponibile");
      }

      console.log("Recupero pagamenti con:", {
        customerId,
        accountId
      });

      // Utilizziamo solo customerId per la ricerca dei pagamenti
      const response = await apiService.get(
        `${import.meta.env.VITE_API_BASE_URL}/customerPayments?customerId=${customerId}&accountId=${accountId}`,
        {
          headers: {
            'auth-token': localStorage.getItem('installamelo-auth-token'),
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Gestiamo correttamente la risposta dal server che potrebbe avere formati diversi
      if (response.success && response.data) {
        // Formato: { success: true, data: [...], message: '...' }
        setPayments(response.data);
      } else if (response.data) {
        // Formato: { data: [...], message: '...' }
        setPayments(response.data);
      } else {
        // Se non abbiamo dati, impostiamo un array vuoto
        setPayments([]);
        // Non lanciamo un errore in questo caso, ma solo un messaggio informativo
        console.log("Nessun pagamento trovato per questo cliente");
      }
    } catch (err) {
      console.error("Errore caricamento pagamenti:", err);
      setError(err.message || "Si è verificato un errore durante il caricamento dei pagamenti");
    } finally {
      setLoading(false);
    }
  };

  // Carica i pagamenti all'avvio
  useEffect(() => {
    if (customerId && accountId) {
      loadPayments();
    }
  }, [customerId, accountId]);

  // Gestisce la visualizzazione dei dettagli di un pagamento
  const handleViewDetails = (payment) => {
    setSelectedPayment(payment);
    setShowModal(true);
  };

  // Traduce lo stato del pagamento in italiano
  const getStatusLabel = (status) => {
    const statusMap = {
      pending: { label: "In Attesa", color: "bg-yellow-100 text-yellow-800" },
      completed: { label: "Completato", color: "bg-green-100 text-green-800" },
      failed: { label: "Fallito", color: "bg-red-100 text-red-800" },
      refunded: { label: "Rimborsato", color: "bg-blue-100 text-blue-800" },
      cancelled: { label: "Annullato", color: "bg-gray-100 text-gray-800" }
    };

    return statusMap[status] || { label: status, color: "bg-gray-100 text-gray-800" };
  };

  // Ottiene l'icona appropriata per il metodo di pagamento
  const getPaymentMethodIcon = (method) => {
    const methodIcons = {
      "credit_card": "fa-credit-card",
      "bank_transfer": "fa-university",
      "paypal": "fa-paypal",
      "stripe": "fa-cc-stripe",
      "cash": "fa-money-bill",
    };

    return methodIcons[method] || "fa-credit-card";
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <PageHeader title="I Miei Pagamenti" />

      {loading ? (
        <div className="flex justify-center items-center py-16">
          <Spinner />
        </div>
      ) : error ? (
        <Alert 
          type="error" 
          message={error} 
          onClose={() => setError(null)} 
        />
      ) : payments.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <i className="fas fa-receipt text-5xl text-gray-300 mb-4"></i>
          <h3 className="text-xl font-medium text-gray-700 mb-2">Nessun pagamento trovato</h3>
          <p className="text-gray-500">
            Non ci sono pagamenti registrati per il tuo account.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Riferimento
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Importo
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Metodo
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stato
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payments.map((payment) => {
                  const statusInfo = getStatusLabel(payment.status);
                  
                  return (
                    <tr key={payment._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {payment.referenceType === 'assistance_request' ? 'Richiesta Assistenza' : payment.referenceType}
                        </div>
                        <div className="text-sm text-gray-500">#{payment.referenceId ? payment.referenceId.substring(0, 8) : 'N/D'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(payment.paymentDate || payment.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(payment.amount, payment.currency || 'EUR')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500">
                          <i className={`fas ${getPaymentMethodIcon(payment.paymentMethod)} mr-2`}></i>
                          {payment.paymentMethod}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-3">
                          <button 
                            onClick={() => handleViewDetails(payment)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Dettagli
                          </button>
                          
                          {/* Mostra il pulsante "Completa pagamento" per tutti i pagamenti non completati */}
                          {payment.status !== 'completed' && (
                            <a 
                              href={`/customerPaymentPage?amount=${payment.amount}&paymentId=${payment._id}`}
                              className="text-green-600 hover:text-green-900 flex items-center"
                            >
                              <i className="fas fa-credit-card mr-1"></i>
                              Paga ora
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal Dettagli Pagamento */}
      {showModal && selectedPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-md w-full mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Dettagli Pagamento</h3>
                <button 
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
            
            <div className="px-6 py-4">
              <dl className="grid grid-cols-1 gap-y-4">
                <div className="sm:grid sm:grid-cols-3 sm:gap-4">
                  <dt className="text-sm font-medium text-gray-500">ID Transazione</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {selectedPayment.transactionId || 'N/D'}
                  </dd>
                </div>
                
                <div className="sm:grid sm:grid-cols-3 sm:gap-4">
                  <dt className="text-sm font-medium text-gray-500">Importo</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 font-medium">
                    {formatCurrency(selectedPayment.amount, selectedPayment.currency || 'EUR')}
                  </dd>
                </div>
                
                <div className="sm:grid sm:grid-cols-3 sm:gap-4">
                  <dt className="text-sm font-medium text-gray-500">Data</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {formatDate(selectedPayment.paymentDate || selectedPayment.createdAt, true)}
                  </dd>
                </div>
                
                <div className="sm:grid sm:grid-cols-3 sm:gap-4">
                  <dt className="text-sm font-medium text-gray-500">Metodo</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <div className="flex items-center">
                      <i className={`fas ${getPaymentMethodIcon(selectedPayment.paymentMethod)} mr-2`}></i>
                      {selectedPayment.paymentMethod}
                    </div>
                  </dd>
                </div>
                
                <div className="sm:grid sm:grid-cols-3 sm:gap-4">
                  <dt className="text-sm font-medium text-gray-500">Stato</dt>
                  <dd className="mt-1 sm:mt-0 sm:col-span-2">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusLabel(selectedPayment.status).color}`}>
                      {getStatusLabel(selectedPayment.status).label}
                    </span>
                  </dd>
                </div>
                
                {selectedPayment.description && (
                  <div className="sm:grid sm:grid-cols-3 sm:gap-4">
                    <dt className="text-sm font-medium text-gray-500">Descrizione</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {selectedPayment.description}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
            
            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
